import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { usePortalAuth } from '../hooks/usePortalAuth'
import styled from '@emotion/styled'
import RoutePath from '../../../pure-js/libs/RoutePath'
import { canAccessProtectedPortal } from '../libs/PortalAuthHelper'
import { PortalLoginLoadingContext, PortalLoginLoadingView } from './portal/PortalLoginLoadingView'

interface ProtectedRouteProps {
  children: React.ReactNode
}

export const shouldShowPortalClaimsLoadingGate = (
  pathname: string,
  loading: boolean,
  portalUser: { uid: string } | null,
  portalClaimsReady: boolean
): boolean => {
  const isLogoutRoute = pathname === RoutePath.PORTAL_LOGOUT
  const waitingForClaims = portalUser !== null && !portalClaimsReady
  return loading || (waitingForClaims && !isLogoutRoute)
}

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #ffffff;
`

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { portalUser, loading, portalClaimsReady } = usePortalAuth()
  const location = useLocation()
  if (shouldShowPortalClaimsLoadingGate(location.pathname, loading, portalUser, portalClaimsReady)) {
    return (
      <LoadingContainer>
        <PortalLoginLoadingView context={PortalLoginLoadingContext.PreparePortal} />
      </LoadingContainer>
    )
  }

  if (!canAccessProtectedPortal(portalUser)) {
    // Redirect to login, preserving intended destination
    // Don't preserve logout route as the intended destination
    const shouldPreserveLocation = location.pathname !== RoutePath.PORTAL_LOGOUT
    const state = shouldPreserveLocation ? { from: location } : undefined

    return <Navigate to={RoutePath.PORTAL_LOGIN} state={state} replace />
  }

  return <>{children}</>
}
